import React from 'react'
import { css } from '@emotion/core'
import moment from 'moment'
import { initializeSelectBoxStyle } from '@/constants/form'
import { EditRoomType } from '@/hooks/use-edit-guest-rooms'

type RoomNumberSelectProps = {
  room: EditRoomType
  roomIndex: number
  onEditRoomNumber: (e: React.ChangeEvent<HTMLSelectElement>, selectRoomType: string, selectIndex: number) => void
  assignPanelEditMode: boolean
  disabled?: boolean
  editGuestRooms?: EditRoomType[]
}

export const RoomNumberSelect: React.FC<RoomNumberSelectProps> = ({
  room,
  roomIndex,
  onEditRoomNumber,
  assignPanelEditMode,
  disabled,
  editGuestRooms,
}) => {
  const assignSelectStyle = css(initializeSelectBoxStyle, {
    width: '100%',
    height: 32,
    color: '#272727',
    border: '1px solid #CCCCCC',
    borderRadius: 19,
    paddingLeft: 16,
    backgroundImage: assignPanelEditMode ? `url(${require('@/static/images/arrow_down_yellow.svg')})` : 'none',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: '95% center',
    backgroundSize: 10,
    display: 'block',
  })

  const isDuplicateOtherEditRoom = (selectOptionGuestRoomId: string): boolean => {
    return (editGuestRooms || []).some(other => {
      if (other.guestRoomId !== selectOptionGuestRoomId) {
        return false
      }
      const otherCheckin = other.assignCheckinDate ? moment(other.assignCheckinDate).startOf('day') : undefined
      const otherCheckout = other.assignCheckoutDate ? moment(other.assignCheckoutDate).startOf('day') : undefined
      const currentCheckin = room.assignCheckinDate ? moment(room.assignCheckinDate).startOf('day') : undefined
      const currentCheckout = room.assignCheckoutDate ? moment(room.assignCheckoutDate).startOf('day') : undefined
      const unknownPeriod = !otherCheckin || !otherCheckout || !currentCheckin || !currentCheckout
      if (unknownPeriod) {
        return true
      }
      return currentCheckin.isBefore(otherCheckout) && currentCheckout.isAfter(otherCheckin)
    })
  }

  /**
   * List generation: List of currently selectable room numbers
   */
  const guestRoomNumberList = (roomInfo: EditRoomType) => {
    const list = [
      {
        name: '部屋番号を選択',
        value: '',
        guestRoomId: '',
      },
    ]

    if (roomInfo.selectableRooms) {
      // If room type is selected
      if (roomInfo && roomInfo.typeNameJa) {
        roomInfo.selectableRooms.forEach(data => {
          if (data.rooms && data.rooms.length && data.typeNameJa === roomInfo.typeNameJa) {
            data.rooms.forEach(room => {
              list.push({
                name: room.roomNumber,
                value: room.guestRoomId,
                guestRoomId: room.guestRoomId,
              })
            })
          }
        })
        if (roomInfo.roomNumber && roomInfo.guestRoomId) {
          let isDuplicate = false
          list.filter(v => {
            if (v.value === roomInfo.guestRoomId) isDuplicate = true
          })
          // Add if not duplicate
          if (!isDuplicate) {
            list.push({
              name: roomInfo.roomNumber,
              value: roomInfo.guestRoomId,
              guestRoomId: roomInfo.guestRoomId,
            })
          }
        }
        // Sort Ascending
        list.sort((a, b) => Number(a.name) - Number(b.name))
      } else {
        // Show all available room numbers if no room type is selected
        const allNumber: { guestRoomId: string; number: string }[] = []
        roomInfo.selectableRooms.forEach(data => {
          if (data.rooms && data.rooms.length) {
            data.rooms.map(room => {
              allNumber.push({
                guestRoomId: room['guestRoomId'],
                number: room['number'],
              })
            })
          }
        })
        allNumber?.forEach(guestRoom => {
          list.push({
            name: guestRoom['number'],
            value: guestRoom['number'],
            guestRoomId: guestRoom['guestRoomId'],
          })
        })
      }
    }

    return list
  }

  return (
    <select
      name={`roomNumber[${roomIndex}]`}
      css={[assignSelectStyle, disabled ? { backgroundColor: '#F5F5F5' } : {}]}
      value={room && room.guestRoomId !== null ? room.guestRoomId : ''}
      onChange={e => onEditRoomNumber(e, e.target.value, roomIndex)}
      disabled={disabled}
    >
      {guestRoomNumberList(room).map(roomNumberItem => {
        if (
          roomNumberItem['guestRoomId'] &&
          roomNumberItem['name'] !== '指定なし' &&
          room.guestRoomId !== roomNumberItem['guestRoomId'] &&
          isDuplicateOtherEditRoom(roomNumberItem['guestRoomId'])
        ) {
          return null
        }
        return (
          <option key={roomNumberItem['guestRoomId']} value={roomNumberItem['value']}>
            {roomNumberItem['name']}
          </option>
        )
      })}
    </select>
  )
}
